import { extractTimeFromText } from './importLogsParsingTime';
import { resolveImportYearForMonthDay } from './importLogsYearResolution';
import {
  CARE_DATE_MONTH_NAME_PATTERN,
  CARE_DATE_WEEKDAY_NAME_PATTERN,
  CARE_EXPLICIT_TIME_PATTERN,
  CARE_OPTIONAL_EXPLICIT_TIME_PATTERN,
  normalizeCareDateTimeText,
} from '../../../shared/careDateTimePatterns';

const MONTH_NAME_PATTERN = CARE_DATE_MONTH_NAME_PATTERN;
const WEEKDAY_NAME_PATTERN = CARE_DATE_WEEKDAY_NAME_PATTERN;
const MONTH_YEAR_HEADING_PATTERN = new RegExp(`^(?<month>${MONTH_NAME_PATTERN})\\s+(?<year>\\d{4})$`, 'i');
const EXACT_DATE_HEADING_PATTERNS = [
  new RegExp(`^(?<month>${MONTH_NAME_PATTERN})\\s+(?<day>\\d{1,2})(?:st|nd|rd|th)?(?:,)?\\s+(?<year>\\d{4})$`, 'i'),
  /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})$/,
  /^(?<month>\d{1,2})\/(?<day>\d{1,2})\/(?<year>\d{4})$/,
];
const MONTH_DAY_HEADING_PATTERN = new RegExp(`^(?<month>${MONTH_NAME_PATTERN})\\s+(?<day>\\d{1,2})(?:st|nd|rd|th)?$`, 'i');
const WEEKDAY_PREFIX_PATTERN = new RegExp(`^(?<weekday>${WEEKDAY_NAME_PATTERN})(?:,)?\\s+`, 'i');
const TRAILING_TIME_PATTERN = new RegExp(`(?:,)?\\s*(?<time>${CARE_EXPLICIT_TIME_PATTERN})$`, 'i');
const LEADING_IMPORT_DATE_HEADING_PATTERNS = [
  new RegExp(`^(?<heading>(?:(?:${WEEKDAY_NAME_PATTERN})(?:,)?\\s+)?${MONTH_NAME_PATTERN}\\s+\\d{4})(?<body>.*)$`, 'i'),
  new RegExp(`^(?<heading>(?:(?:${WEEKDAY_NAME_PATTERN})(?:,)?\\s+)?${MONTH_NAME_PATTERN}\\s+\\d{1,2}(?:st|nd|rd|th)?(?:,)?\\s+\\d{4}${CARE_OPTIONAL_EXPLICIT_TIME_PATTERN})(?<body>.*)$`, 'i'),
  new RegExp(`^(?<heading>(?:(?:${WEEKDAY_NAME_PATTERN})(?:,)?\\s+)?\\d{4}-\\d{2}-\\d{2})(?<body>.*)$`, 'i'),
  new RegExp(`^(?<heading>(?:(?:${WEEKDAY_NAME_PATTERN})(?:,)?\\s+)?\\d{1,2}\\/\\d{1,2}\\/\\d{4})(?<body>.*)$`, 'i'),
  new RegExp(`^(?<heading>(?:(?:${WEEKDAY_NAME_PATTERN})(?:,)?\\s+)?${MONTH_NAME_PATTERN}\\s+\\d{1,2}(?:st|nd|rd|th)?${CARE_OPTIONAL_EXPLICIT_TIME_PATTERN})(?<body>.*)$`, 'i'),
];
const INLINE_IMPORT_DATE_PATTERN = new RegExp(
  `(?:\\bon\\s+)?(?<heading>(?:(?:${WEEKDAY_NAME_PATTERN})(?:,)?\\s+)?${MONTH_NAME_PATTERN}\\s+\\d{1,2}(?:st|nd|rd|th)?(?:,)?(?:\\s+\\d{4})?${CARE_OPTIONAL_EXPLICIT_TIME_PATTERN})(?!\\d)`,
  'ig'
);
const STARTS_WITH_IMPORT_RANGE_CONNECTOR_PATTERN = /^(?:through|to|until|-|–|—)\b/i;
const IMPORT_RANGE_CONNECTOR_PATTERN = /\b(?:through|to|until)\b/i;
const MONTH_INDEX_BY_NAME = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

const stripImportWeekdayPrefix = (value = '') => String(value || '').replace(WEEKDAY_PREFIX_PATTERN, '').trim();
const stripOrdinalDaySuffix = (value = '') => String(value || '').replace(/\b(\d{1,2})(?:st|nd|rd|th)\b/ig, '$1');

export const normalizeImportDateAnchorCandidate = (value = '') => normalizeCareDateTimeText(value);

const extractImportHeadingTime = (value = '') => {
  const source = normalizeImportDateAnchorCandidate(value);
  const match = source.match(TRAILING_TIME_PATTERN);
  if (!match?.groups?.time) {
    return {
      headingWithoutTime: source,
      timeValue: null,
      timeLabel: '',
    };
  }

  const normalizedTime = extractTimeFromText(String(match.groups.time || '').trim()).time || null;
  return {
    headingWithoutTime: source.slice(0, match.index).trim().replace(/[,:-]+$/u, '').trim(),
    timeValue: normalizedTime,
    timeLabel: String(match.groups.time || '').trim(),
  };
};

export const getImportDateHeading = (line = '', { defaultYear = null, fallbackDate = null, sourceText = '' } = {}) => {
  const trimmed = normalizeImportDateAnchorCandidate(String(line || '').trim().replace(/[,:-]\s*$/, '').trim());
  if (!trimmed) {
    return null;
  }

  const {
    headingWithoutTime,
    timeValue,
    timeLabel,
  } = extractImportHeadingTime(trimmed);
  const withoutWeekday = stripImportWeekdayPrefix(headingWithoutTime);
  const comparableHeading = stripOrdinalDaySuffix(withoutWeekday).trim();
  const dateLabel = withoutWeekday || headingWithoutTime || trimmed;

  const monthYearMatch = comparableHeading.match(MONTH_YEAR_HEADING_PATTERN);
  if (monthYearMatch?.groups?.month && monthYearMatch.groups?.year) {
    const monthKey = monthYearMatch.groups.month.slice(0, 3).toLowerCase();
    const month = MONTH_INDEX_BY_NAME[monthKey];
    const year = Number(monthYearMatch.groups.year);
    if (month !== undefined && Number.isFinite(year)) {
      return {
        date: new Date(year, month, 1),
        dateLabel,
        datePrecision: 'month',
        headingText: trimmed,
        timeValue,
        timeLabel,
      };
    }
  }

  for (const pattern of EXACT_DATE_HEADING_PATTERNS) {
    const match = comparableHeading.match(pattern);
    if (!match?.groups) {
      continue;
    }

    if (match.groups.month && match.groups.day && match.groups.year) {
      const monthPart = match.groups.month;
      const monthIndex = /^[A-Za-z]/.test(monthPart)
        ? MONTH_INDEX_BY_NAME[monthPart.slice(0, 3).toLowerCase()]
        : Number(monthPart) - 1;
      const day = Number(match.groups.day);
      const year = Number(match.groups.year);
      if (monthIndex !== undefined && Number.isFinite(day) && Number.isFinite(year)) {
        return {
          date: new Date(year, monthIndex, day),
          dateLabel,
          datePrecision: 'day',
          headingText: trimmed,
          timeValue,
          timeLabel,
        };
      }
    }
  }

  const monthDayMatch = comparableHeading.match(MONTH_DAY_HEADING_PATTERN);
  if (monthDayMatch?.groups?.month && monthDayMatch.groups?.day) {
    const monthIndex = MONTH_INDEX_BY_NAME[monthDayMatch.groups.month.slice(0, 3).toLowerCase()];
    const day = Number(monthDayMatch.groups.day);
    const yearResolution = resolveImportYearForMonthDay({
      monthIndex,
      day,
      fallbackDate: fallbackDate || defaultYear || new Date(),
      sourceText: sourceText || trimmed,
    });
    const year = Number(yearResolution.year);
    if (monthIndex !== undefined && Number.isFinite(day) && Number.isFinite(year)) {
      return {
        date: new Date(year, monthIndex, day),
        dateLabel,
        datePrecision: 'day',
        headingText: trimmed,
        timeValue,
        timeLabel,
        yearResolutionStrategy: yearResolution.strategy,
        yearAdjustedFromFuture: yearResolution.adjustedToPast,
      };
    }
  }

  const wordCount = comparableHeading.split(/\s+/).filter(Boolean).length;
  const looksLikeLooseDate = (
    wordCount <= 4
    && comparableHeading.length <= 32
    && (
      /\d/.test(comparableHeading)
      || new RegExp(MONTH_NAME_PATTERN, 'i').test(comparableHeading)
    )
  );

  if (!looksLikeLooseDate) {
    return null;
  }

  const parsed = new Date(comparableHeading);
  if (parsed instanceof Date && !Number.isNaN(parsed.getTime())) {
    const isMonthHeading = /^\s*[A-Za-z]+\s+\d{4}\s*$/i.test(comparableHeading);
    return {
      date: new Date(parsed),
      dateLabel,
      datePrecision: isMonthHeading ? 'month' : 'day',
      headingText: trimmed,
      timeValue,
      timeLabel,
    };
  }

  return null;
};

export const getLeadingImportDateHeading = (line = '', {
  defaultYear = null,
  fallbackDate = null,
  sourceText = '',
} = {}) => {
  const trimmed = String(line || '').trim();
  if (!trimmed) {
    return null;
  }

  for (const pattern of LEADING_IMPORT_DATE_HEADING_PATTERNS) {
    const match = trimmed.match(pattern);
    const headingText = String(match?.groups?.heading || '').trim();
    if (!headingText) {
      continue;
    }

    const heading = getImportDateHeading(headingText, {
      defaultYear,
      fallbackDate,
      sourceText: sourceText || trimmed,
    });
    if (!heading?.date) {
      continue;
    }

    return {
      ...heading,
      bodyText: String(match?.groups?.body || '')
        .replace(/^[\s,.:;\-–—]+/u, '')
        .trimStart(),
      sourceText: trimmed,
    };
  }

  return null;
};

export const getInlineImportDateHeading = (text = '', {
  defaultYear = null,
  fallbackDate = null,
  sourceText = '',
} = {}) => {
  const source = String(text || '').trim();
  if (!source) {
    return null;
  }

  const matches = Array.from(source.matchAll(INLINE_IMPORT_DATE_PATTERN));
  if (matches.length !== 1) {
    return null;
  }

  const match = matches[0];
  const headingText = String(match?.groups?.heading || '').trim();
  if (!headingText) {
    return null;
  }

  const trailingText = source.slice((match.index || 0) + match[0].length).trim();
  if (STARTS_WITH_IMPORT_RANGE_CONNECTOR_PATTERN.test(trailingText)) {
    return null;
  }

  const leadingText = source.slice(0, match.index || 0);
  const surroundingText = `${leadingText} ${trailingText}`.trim();
  if (/\bstreak\b/i.test(surroundingText) && IMPORT_RANGE_CONNECTOR_PATTERN.test(source)) {
    return null;
  }

  const heading = getImportDateHeading(headingText, {
    defaultYear,
    fallbackDate,
    sourceText: sourceText || source,
  });
  if (!heading?.date) {
    return null;
  }

  return {
    ...heading,
    sourceText: source,
  };
};

export const getRepeatedInlineImportDateMatches = (text = '') => {
  const source = String(text || '').trim();
  if (!source) {
    return [];
  }

  const matches = Array.from(source.matchAll(INLINE_IMPORT_DATE_PATTERN))
    .map((match) => ({
      index: Number(match?.index || 0),
      headingText: String(match?.groups?.heading || '').trim(),
    }))
    .filter((match) => match.headingText);

  if (matches.length <= 1) {
    return matches;
  }

  return matches.filter((match, index) => {
    if (index === 0) {
      return true;
    }

    const characterBefore = source.charAt(Math.max(0, match.index - 1));
    return !characterBefore || /[\s,.;:!?()\-–—]/u.test(characterBefore);
  });
};
